import React, { forwardRef, useEffect, useState } from 'react';
import { styled } from '@mui/material';

import {
    AIFeedbackSection,
    AssociatedTasksSection,
    AttachmentSection,
    FeedbackSection,
    SubmissionView,
    SubmissionSection,
    SubtaskSection,
    TaskMainSection,
} from 'components/organisms/Task/TaskSection';
import { useUser2 } from 'contexts/user2';
import { Button, Divider, Dropdown, Icon, IconButton, Stack, Text } from 'shell/components';
import { TYPES } from 'utils/constants';

const TaskDrawerInner = forwardRef(
    (
        {
            device,
            role,
            isOpen,
            taskList,
            task,
            currentAccountId,
            assignableGroupMembers,
            courseInstructors,
            getUserByAccountId,
            openCreateTaskDrawer,
            onOpenTask,
            updateTask,
            deleteTask,
            duplicateTask,
            createTaskResource,
            deleteTaskResource,
            getSubtasksByTaskId,
            getSubtaskById,
            createSubtask,
            updateSubtask,
            deleteSubtask,
            feedback,
            addFeedback,
            updateFeedback,
            deleteFeedback,
            addSubfeedback,
            updateSubfeedback,
            deleteSubfeedback,
            countFeedback,
            fileList,
            deleteFile,
            countFiles,
            sharedUrlList,
            createActivity,
            onClose,
            subtaskState,
            setSubtaskState,
            submissionsState,
            setSubmissionsState,
            ...rest
        },
        ref
    ) => {
        const { formatives } = useUser2();

        const [resourcesModal, setResourcesModal] = useState({ isOpen: false });

        const handleSelect = (key) => {
            switch (key) {
                case 'delete': {
                    deleteTask(task.id);
                    break;
                }
                case 'duplicate': {
                    duplicateTask(task.id);
                    break;
                }
                default:
                    break;
            }
        };

        const handleClickAddSubtask = () => {
            const subtask = createSubtask(task.id);
            setSubtaskState({
                isOpen: true,
                subtaskId: subtask.id,
            });
        };

        const actionOptions = [
            { key: 'delete', value: 'Delete', disabled: [TYPES.TEMPLATE, TYPES.MILESTONE].includes(task?.type) },
            {
                key: 'duplicate',
                value: 'Duplicate',
                disabled: [TYPES.TEMPLATE, TYPES.MILESTONE].includes(task?.type) || task?.isLocal,
            },
        ];

        const renderBreadcrumbs = () => {
            if (!task) return null;
            if (task.type === TYPES.MILESTONE) return null;

            const milestone = Object.values(taskList.all)
                .filter(({ type }) => type === TYPES.MILESTONE)
                .find(({ milestone_id }) => milestone_id === task.milestone_id);
            const subtask = subtaskState.isOpen ? getSubtaskById(subtaskState.subtaskId) : null;

            return milestone || subtask ? (
                <BreadcrumbsStack direction={'row'} spacing={1}>
                    {milestone && (
                        <>
                            <Button
                                color={'neutral'}
                                startIcon={<Icon kind={'starEmpty'} />}
                                variant={'borderless'}
                                onClick={() => onOpenTask(milestone.id)}
                            >
                                <Text
                                    sx={{ textDecoration: 'underline' }}
                                    color={'inherit'}
                                    noWrap
                                    title={milestone.title}
                                    truncate={30}
                                    variant={'p2'}
                                >
                                    {milestone.title}
                                </Text>
                            </Button>
                            <Icon kind={'chevronRight'} />
                        </>
                    )}
                    <Button
                        color={'neutral'}
                        variant={'borderless'}
                        onClick={() => setSubtaskState({ isOpen: false, subtaskId: '' })}
                    >
                        <Text
                            sx={{ textDecoration: 'underline' }}
                            color={'inherit'}
                            noWrap
                            title={task.title}
                            truncate={30}
                            variant={'p2'}
                        >
                            {task.title}
                        </Text>
                    </Button>
                    {subtask && (
                        <>
                            <Icon kind={'chevronRight'} />
                            <Button color={'neutral'} variant={'borderless'}>
                                <Text
                                    sx={{ textDecoration: 'underline' }}
                                    color={'inherit'}
                                    noWrap
                                    title={subtask.title}
                                    truncate={30}
                                    variant={'p2'}
                                >
                                    {subtask.title}
                                </Text>
                            </Button>
                        </>
                    )}
                </BreadcrumbsStack>
            ) : null;
        };

        useEffect(() => {
            if (!isOpen) {
                setTimeout(() => {
                    setSubtaskState({
                        isOpen: false,
                        subtaskId: '',
                    });
                    setSubmissionsState({
                        isOpen: false,
                        mode: 'history',
                        submissions: [],
                    });
                }, 100);
            }
        }, [isOpen]);

        useEffect(() => {
            setSubmissionsState({
                isOpen: false,
                mode: 'history',
                submissions: [],
            });
        }, [task?.id]);

        if (subtaskState.isOpen) {
            return (
                <Stack ref={ref} {...rest}>
                    <IconButton
                        data-cy={'subtask-back-button'}
                        aria-label={'back'}
                        kind={'arrowLeft'}
                        size={'medium'}
                        variant={'square'}
                        onClick={() => setSubtaskState({ isOpen: false, subtaskId: '' })}
                    />
                    {renderBreadcrumbs()}
                    <TaskMainSection
                        device={device}
                        currentAccountId={currentAccountId}
                        role={role}
                        type={'subtask'}
                        task={getSubtaskById(subtaskState.subtaskId)}
                        taskList={taskList}
                        assignableGroupMembers={assignableGroupMembers}
                        courseInstructors={courseInstructors}
                        getUserByAccountId={getUserByAccountId}
                        updateTask={updateSubtask}
                        createTaskResource={createTaskResource}
                        deleteTaskResource={deleteTaskResource}
                        createActivity={createActivity}
                        resourcesModal={resourcesModal}
                        setResourcesModal={setResourcesModal}
                    />
                </Stack>
            );
        }

        if (submissionsState.isOpen) {
            return (
                <Stack ref={ref} spacing={2} {...rest}>
                    <IconButton
                        aria-label={'back'}
                        kind={'arrowLeft'}
                        size={'medium'}
                        variant={'square'}
                        onClick={() => setSubmissionsState({ isOpen: false, mode: 'history', submissions: [] })}
                    />
                    <SubmissionView
                        submissionsState={submissionsState}
                        setSubmissionsState={setSubmissionsState}
                        getUserByAccountId={getUserByAccountId}
                    />
                </Stack>
            );
        }

        return (
            <Stack ref={ref} divider={<Divider spacing={2} />} spacing={2} {...rest}>
                <Stack className={'task-4'}>
                    <Stack direction={'row'} justifyContent={'space-between'}>
                        <IconButton
                            data-cy={'tasks-back-button'}
                            aria-label={'back'}
                            id={'task-back-button'}
                            kind={'arrowLeft'}
                            size={'medium'}
                            variant={'square'}
                            onClick={onClose}
                        />
                        <Dropdown
                            id={`dropdown-${task?.id}`}
                            options={actionOptions}
                            rootComponent={
                                <IconButton
                                    aria-label={'more task options'}
                                    kind={'moreHorizontal'}
                                    size={'medium'}
                                    variant={'square'}
                                />
                            }
                            onSelect={({ key }) => handleSelect(key)}
                        />
                    </Stack>
                    {renderBreadcrumbs()}
                    <TaskMainSection
                        device={device}
                        currentAccountId={currentAccountId}
                        role={role}
                        disabled={task?.isLocal}
                        isDrawerOpen={isOpen}
                        task={task}
                        taskList={taskList}
                        fileList={fileList}
                        sharedUrlList={sharedUrlList}
                        assignableGroupMembers={assignableGroupMembers}
                        courseInstructors={courseInstructors}
                        getUserByAccountId={getUserByAccountId}
                        updateTask={updateTask}
                        createTaskResource={createTaskResource}
                        deleteTaskResource={deleteTaskResource}
                        createActivity={createActivity}
                        resourcesModal={resourcesModal}
                        setResourcesModal={setResourcesModal}
                    />
                </Stack>
                {task?.type === TYPES.MILESTONE ? (
                    <>
                        <Stack divider={<Divider />} spacing={2}>
                            <AssociatedTasksSection
                                tasks={Object.values(taskList.all).filter(
                                    ({ active, type, milestone_id }) =>
                                        active && type !== TYPES.MILESTONE && milestone_id === task.milestone_id
                                )}
                                getUserByAccountId={getUserByAccountId}
                                onClickTask={onOpenTask}
                                onClickCreate={() => openCreateTaskDrawer('toDo', task.milestone_id)}
                                updateTaskStatus={(id, status) => updateTask(id, 'status', status, true)}
                            />
                            {formatives?.find(({ milestone_id }) => milestone_id === task.milestone_id)?.status ===
                                'complete' && <AIFeedbackSection task={task} />}
                            <SubmissionSection
                                isOpen={isOpen}
                                task={task}
                                getUserByAccountId={getUserByAccountId}
                                setSubmissionsState={setSubmissionsState}
                                updateTask={updateTask}
                            />
                        </Stack>
                        <FooterStack
                            id={'milestone-submission-footer'}
                            alignItems={'center'}
                            direction={'row-reverse'}
                            justifyContent={'space-between'}
                        />
                    </>
                ) : (
                    <Stack divider={<Divider />} spacing={2}>
                        <SubtaskSection
                            disabled={task?.isLocal}
                            isTaskDrawerOpen={isOpen}
                            subtasks={getSubtasksByTaskId(task?.id)}
                            updateSubtaskStatus={(id, status) => updateSubtask(id, 'status', status)}
                            deleteSubtask={deleteSubtask}
                            getUserByAccountId={getUserByAccountId}
                            onAddSubtask={handleClickAddSubtask}
                            onOpenSubtask={(id) => setSubtaskState({ isOpen: true, subtaskId: id })}
                        />
                        <AttachmentSection
                            disabled={task?.isLocal}
                            taskId={task?.id}
                            fileList={fileList}
                            currentAccountId={currentAccountId}
                            deleteFile={deleteFile}
                            countFiles={countFiles}
                        />
                        <FeedbackSection
                            disabled={task?.isLocal}
                            taskId={task?.id}
                            currentAccountId={currentAccountId}
                            feedback={feedback}
                            fileList={fileList}
                            getUserByAccountId={getUserByAccountId}
                            addFeedback={addFeedback}
                            updateFeedback={updateFeedback}
                            deleteFeedback={deleteFeedback}
                            addSubfeedback={addSubfeedback}
                            updateSubfeedback={updateSubfeedback}
                            deleteSubfeedback={deleteSubfeedback}
                            countFeedback={countFeedback}
                        />
                    </Stack>
                )}
            </Stack>
        );
    }
);

const FooterStack = styled(Stack)(({ theme }) => {
    return {
        position: 'absolute',
        bottom: 0,
        right: 0,
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        width: '100%',
        height: '68px',
        padding: theme.spacing(4),
        background: theme.palette.ensightful.shades[0],
    };
});

const BreadcrumbsStack = styled(Stack)(({ theme }) => {
    return {
        flexWrap: 'wrap',
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(0.5),
    };
});

export default TaskDrawerInner;
